import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, PlusIcon } from '@shared/ui';
import type { ComposerModule, ModulesStatus } from '../model/useComposerModule';

interface ModuleSlotProps {
  status: ModulesStatus;
  module: ComposerModule | null;
  menuOpen: boolean;
  menuId: string;
  disabled?: boolean;
  onOpen: () => void;
  /** Список модулей не загрузился — «Повторить». */
  onRetry: () => void;
  /** Модулей нет — модалка нового модуля. */
  onCreate: () => void;
}

const BASE =
  'flex h-11 w-full items-center gap-2 rounded-[14px] border border-solid border-line bg-elev px-3 text-left text-[14px] font-medium text-fg transition-colors active:border-[#3a3a40] disabled:opacity-60';

/**
 * Строка модуля на этапе 2 телефона (ТЗ главной § 7 п. 4), над составом — в зоне большого пальца:
 * строки внизу на этом экране не видно, и без неё ученик не знает, куда ляжет карточка. Касание
 * открывает меню модулей. Показывает то же, что чип строки: модуль — одно состояние виджета.
 */
export function ModuleSlot({ status, module, menuOpen, menuId, disabled = false, onOpen, onRetry, onCreate }: ModuleSlotProps) {
  const { t } = useTranslation();

  if (!module && status === 'loading') {
    return <span role="status" aria-label={t('homeComposer.module.loading')} className="block h-11 w-full animate-skeleton rounded-[14px] bg-elev" />;
  }

  if (status === 'error') {
    return (
      <button type="button" onClick={onRetry} className={BASE}>
        <span aria-hidden className="text-warn">
          ⚠
        </span>
        <span className="min-w-0 flex-1 truncate">{t('homeComposer.errors.modulesFailed')}</span>
        <span className="flex-none text-accent-hi">{t('training.retry')}</span>
      </button>
    );
  }

  if (!module) {
    return (
      <button type="button" onClick={onCreate} disabled={disabled} className={BASE}>
        <PlusIcon className="h-3.5 w-3.5 flex-none text-accent" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{t('homeComposer.module.new')}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={disabled}
      aria-label={t('homeComposer.module.aria', { title: module.title })}
      aria-haspopup="dialog"
      aria-expanded={menuOpen}
      aria-controls={menuOpen ? menuId : undefined}
      className={BASE}
    >
      <span aria-hidden className="flex-none text-muted">
        →
      </span>
      <span className="min-w-0 flex-1 truncate">{t('homeComposer.inModule', { title: module.title })}</span>
      <ChevronDownIcon className="h-3.5 w-3.5 flex-none text-muted" aria-hidden />
    </button>
  );
}
